import type { AxiosProgressEvent } from 'axios';
import api from '@/shared/api';
import { buildTruthySearchParams, requestExcelBlobExport } from '@/shared/lib';
import { buildFormData } from '@/shared/lib';
import type {
  CreateTrainingContentPayload,
  CreateTrainingContentResponse,
  PaginatedTrainingContentsResponse,
  TrainingContentDetailResponse,
  UpdateTrainingContentPayload,
  TrainingContentFilters,
  TrainingContentSortField,
  TrainingContentSortOrder,
} from '../model/trainingContent.types';

export interface ListTrainingContentsParams extends TrainingContentFilters {
  page?: number;
  itemsPerPage?: number;
  term?: string;
  sort?: TrainingContentSortField;
  order?: TrainingContentSortOrder;
}

export interface ExportTrainingContentsParams extends TrainingContentFilters {
  term?: string;
  sort?: TrainingContentSortField;
  order?: TrainingContentSortOrder;
}

export interface CreateTrainingContentOptions {
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
  signal?: AbortSignal;
}

const TRAINING_CONTENT_EXPORT_QUERY_KEYS = [
  'term',
  'sort',
  'order',
  'extension',
  'contentType',
  'responsibleMemberId',
  'responsiblePositionId',
  'createdAt',
] as const satisfies readonly (keyof ExportTrainingContentsParams)[];

const EXPORT_FALLBACK_FILENAME = 'lista-conteudos.xlsx';

const EXPORT_GENERIC_RETRY_MESSAGE = 'Tente novamente.';

function buildCreateTrainingContentFormData(payload: CreateTrainingContentPayload): FormData {
  const formData = buildFormData({
    title: payload.title,
    contentType: payload.contentType,
    extension: payload.extension,
    sourceType: payload.sourceType,
    responsibleMemberId: payload.responsibleMemberId,
    responsiblePositionId: payload.responsiblePositionId,
    externalUrl: payload.externalUrl,
  });

  payload.visibleMemberIds.forEach((memberId) => {
    formData.append('visibleMemberIds', String(memberId));
  });

  if (payload.file) {
    formData.append('file', payload.file);
  }

  return formData;
}

class TrainingContentApi {
  async listTrainingContents(
    params: ListTrainingContentsParams = {}
  ): Promise<PaginatedTrainingContentsResponse> {
    const response = await api.get<PaginatedTrainingContentsResponse>('/trainings/contents', {
      params,
    });
    return response.data;
  }

  async getTrainingContent(contentId: number): Promise<TrainingContentDetailResponse> {
    const response = await api.get<TrainingContentDetailResponse>(
      `/trainings/contents/${contentId}`
    );
    return response.data;
  }

  async updateTrainingContent(
    contentId: number,
    payload: UpdateTrainingContentPayload
  ): Promise<TrainingContentDetailResponse> {
    const response = await api.put<TrainingContentDetailResponse>(
      `/trainings/contents/${contentId}`,
      payload
    );
    return response.data;
  }

  async deleteTrainingContent(contentId: number): Promise<void> {
    await api.delete(`/trainings/contents/${contentId}`);
  }

  async exportTrainingContentsExcel(
    params: ExportTrainingContentsParams = {}
  ): Promise<{ blob: Blob; exportedCount: number; filename: string }> {
    const queryParams = buildTruthySearchParams(params, TRAINING_CONTENT_EXPORT_QUERY_KEYS);

    return requestExcelBlobExport(
      () =>
        api.get(`/trainings/contents/export/excel?${queryParams.toString()}`, {
          responseType: 'blob',
        }),
      EXPORT_FALLBACK_FILENAME,
      EXPORT_GENERIC_RETRY_MESSAGE
    );
  }

  async createTrainingContent(
    payload: CreateTrainingContentPayload,
    options: CreateTrainingContentOptions = {}
  ): Promise<CreateTrainingContentResponse> {
    const response = await api.post<CreateTrainingContentResponse>(
      '/trainings/contents',
      buildCreateTrainingContentFormData(payload),
      {
        headers: { 'Content-Type': 'multipart/form-data; charset=UTF-8' },
        onUploadProgress: options.onUploadProgress,
        signal: options.signal,
      }
    );
    return response.data;
  }

  async getTrainingContentFile(contentId: number): Promise<Blob> {
    const response = await api.get<Blob>(`/trainings/contents/${contentId}/file`, {
      responseType: 'blob',
    });
    return response.data;
  }
}

export const trainingContentApi = new TrainingContentApi();
